// fw RecordList — ONE list primitive, two presentations, chosen by how much
// room it actually has.
//
// ── What this replaces, and why it is not a stylesheet fix ──────────────────
//
// This file's other half, DataTable.js, opened with "DESKTOP ONLY. For
// Office." and wrapped itself in `overflow-x: auto`. Every Office list screen
// renders it. Measured in a real browser at 360px (docs/APP_REBUILD.md §1):
//
//   Office Jobs table        683px wide inside a 360px viewport
//   Office Invoices table    442px inside 360px
//   where STATUS lives       column 7 on Jobs — off-screen
//   the same screens @1440   clean, zero overflow
//
// Admins run those screens on phones. The reason nobody caught it is worth
// keeping: the table did not scroll the DOCUMENT, it scrolled inside its own
// `.fw-table-wrap` box, and `scripts/ui-check.js` asserted "no sideways
// scroll" by reading `document.scrollWidth`. It passed 448/448 on a layout
// that cannot be used, and that number was quoted as evidence the app was
// fine. APP_REBUILD.md §1: an invariant that measures the document instead of
// the element that actually scrolls is worse than no invariant.
//
// So the fix could not be a media query on the Jobs screen. The structural
// fault was that a COMPONENT was allowed to declare "desktop only" about
// itself and ~10 screens inherited it. There is now one list primitive, it
// decides its own shape, and a call site cannot opt out of working on a phone
// (see planCard below — `role` defaults to 'fact' and the first column is
// promoted to the headline when nobody claims it).
//
// ── Container width, not viewport width ────────────────────────────────────
//
// The same list is full-bleed on a phone AND sits in the ~380px `aside` of
// Office's Split layout on a 1440px desktop (apps/office/records/Jobs.js
// renders both). Only the container knows which, so the breakpoint is
// resolved with ResizeObserver against the wrapper element. A viewport query
// would give the desktop detail column a 7-column table in 380px — the exact
// defect this file exists to remove, reintroduced at a different width.
//
// The number itself is NOT a new breakpoint: RECORD_CARD_MAX is FW_PHONE_MAX,
// the same 768 as --fw-phone-max. Spec §6: two layouts, not eight.
//
// It does NOT carry usePhoneLayout's landscape clause, and that is correct
// rather than an oversight. That clause exists because a VIEWPORT 844px wide
// can still be a phone; this measurement is of a CONTAINER, and a container
// 844px wide has room for a table whatever device it is on. Asking about the
// device here would put cards in an 844px column for no reason.
//
// ── The fallback, and why it is innerWidth rather than matchMedia ──────────
//
// ResizeObserver does not exist in jsdom, so every existing screen suite would
// otherwise render whatever the first-paint guess happened to be. The fallback
// is `window.innerWidth`, which jest sets to 1024 — so jsdom gets the TABLE
// and the ~10 screen suites that assert against `thead th` keep testing what
// they were written to test.
//
// `matchMedia` would have been the tidier fallback and is deliberately not
// used: jsdom implements it as a stub that answers `matches: false` at every
// width, so a viewport fallback built on it can never be seen to switch, in
// either direction. It would be a fallback with no test — CLAUDE.md rule 8
// calls that a hope, not a guard. innerWidth is settable in a test, so the
// fallback is provable, and RecordList.test.js drives it both ways.

import React, { useEffect, useRef, useState } from 'react';
import { FW_PHONE_MAX } from '../usePhoneLayout.js';
import { Icon } from '../../components/Icon.js';
import './DataTable.css';
import './RecordList.css';

const ALIGNS = ['left', 'right', 'center'];

/** Presentation hints a column may declare. Anything else is a 'fact'. */
export const RECORD_ROLES = ['title', 'subtitle', 'badge', 'fact', 'hidden'];

/**
 * How many facts a card shows. Three is not arbitrary: APP_REBUILD.md §5
 * treats a card taller than 160px on a phone as evidence its text is wrapping
 * to death, and a title + subtitle + badge + four label/value pairs does not
 * fit in 160px at 320px wide. Columns past the third are table-only.
 */
export const RECORD_FACT_LIMIT = 3;

/** The one breakpoint, borrowed rather than invented. */
export const RECORD_CARD_MAX = FW_PHONE_MAX;

function cellClass(col) {
  const align = ALIGNS.includes(col.align) ? col.align : 'left';
  return [`fw-table__cell--${align}`, align === 'right' ? 'fw-num' : ''].filter(Boolean).join(' ');
}

function cellValue(col, row, i) {
  if (!col) return null;
  if (typeof col.render === 'function') return col.render(row, i);
  return row ? row[col.key] : null;
}

/**
 * A column's role, defaulted. An unknown string is a 'fact' rather than an
 * error: a typo'd role must degrade to "shown, labelled, in the card", never
 * to "silently missing on every phone".
 */
export function roleOf(col) {
  return col && RECORD_ROLES.includes(col.role) ? col.role : 'fact';
}

/**
 * The strings a screen prints when it has nothing to print.
 *
 * `textOr()` in apps/office/records/OfficePage.js answers an absent value with
 * an em dash, and a dozen columns render through it. On a TABLE that is right:
 * a cell has to hold the column's place in the grid, and an empty box reads as
 * a rendering fault. On a CARD it is the opposite — a fact is drawn only if it
 * is drawn, so a dash spends one of three slots saying "nothing".
 */
const FACT_PLACEHOLDERS = new Set(['\u2014', '\u2013', '-']);

/**
 * Is this rendered value worth one of a card's three slots?
 *
 * ── Why this exists, measured ───────────────────────────────────────────────
 *
 * RECORD_FACT_LIMIT is 3 and planCard took the first three fact columns
 * whatever they held, so on 2026-09-10 every Office Jobs card at 390px read
 *
 *   Hedge shaping, front drive | TO DO
 *     SCHEDULED 12 Sep   CREW Marcus Reyes   CLIENT —
 *
 * on a job with no client — one slot in three spent on an em dash, while
 * Property, Work list and Address sat in columns 4-6 and were on NO phone
 * card in the app. The fix that shipped before this one was to REORDER the
 * columns, which moves the problem to whichever column is unlucky: any of
 * Client, Property and Work list is blank on a real row, and the order that
 * is best for a job with a client is not the order that is best for one
 * without.
 *
 * So the choice is made per ROW, not per screen. A blank fact yields its slot
 * to the next column that has something in it, and "show more on a phone"
 * stops being a fixed ranking and becomes: show the three that say something.
 *
 * What it can and cannot see, stated rather than assumed: a string, a number,
 * null/undefined/false and an empty array. A column whose `render` returns
 * JSX is ALWAYS kept, even when that JSX would paint a dash — this function is
 * not a renderer and must never guess at one. Columns that want this
 * behaviour return a string, which is what `textOr` already does.
 */
export function isEmptyFact(value) {
  if (value === null || value === undefined || value === false) return true;
  if (typeof value === 'string') {
    const s = value.trim();
    return s === '' || FACT_PLACEHOLDERS.has(s);
  }
  if (Array.isArray(value)) return value.length === 0 || value.every(isEmptyFact);
  return false;
}

/**
 * "No row was passed", as a value no caller can accidentally hold.
 *
 * A plain `undefined` default would not do: planCard(cols, undefined) is what
 * a caller mapping over a sparse array produces, and taking the per-row branch
 * on it means calling every column's `render(undefined)` — a screen full of
 * TypeErrors instead of the old ranking. This sentinel makes "not asked" and
 * "asked about nothing" different questions.
 */
const NO_ROW = Symbol('planCard: no row');

/**
 * Decide what a card shows, from the column descriptors alone.
 *
 * Pure and exported so the rules can be tested without a DOM — the three that
 * matter are the ones an un-migrated call site depends on:
 *
 *   1. `role` defaults to 'fact', so every column of every existing DataTable
 *      call site is already something a card can render.
 *   2. If no column asks to be the title, the FIRST visible column is
 *      promoted. Without this an un-migrated list renders as an anonymous
 *      stack of label/value pairs with no headline — which is what "a screen
 *      cannot opt out of being usable on a phone" has to mean in practice.
 *   3. `role: 'hidden'` is the only way to keep a column off a card, and it is
 *      a per-COLUMN decision. There is no per-screen escape hatch.
 *
 * A second column claiming an already-taken slot (two 'badge' columns, say)
 * falls through to the fact list rather than disappearing. Losing data is the
 * worse failure, and it is the silent one.
 *
 *   4. Given a ROW, a fact slot held by a column that is BLANK on that row is
 *      given to the next column that is not — but only when there is one. The
 *      card never shows fewer facts than the column ranking alone would have
 *      given it, so this can substitute a good fact for a useless one and can
 *      never quietly empty a card. Called without a row it answers exactly
 *      what it always did, which is what keeps the call sites that assert
 *      planCard(COLUMNS) directly honest.
 *
 * @returns {{title: object|null, subtitle: object|null, badge: object|null, facts: object[]}}
 */
/**
 * Which column NAMES the record — the card's headline, and the cell a person
 * clicks in the table.
 *
 * Extracted from planCard on 2026-09-18 so the two renderings cannot disagree.
 * The card has always drawn this column as its title; the table now marks the
 * same cell as the one that opens the record, and "the same" has to be a
 * function rather than two copies of a `find`.
 *
 * `|| cols[0]` covers the pathological all-hidden case: something is still
 * better than a card with nothing in it.
 */
export function titleColumn(columns) {
  const cols = (Array.isArray(columns) ? columns : []).filter(Boolean);
  const visible = cols.filter((c) => roleOf(c) !== 'hidden');
  return visible.find((c) => roleOf(c) === 'title') || visible[0] || cols[0] || null;
}

export function planCard(columns, row = NO_ROW, index = 0) {
  const cols = (Array.isArray(columns) ? columns : []).filter(Boolean);
  const visible = cols.filter((c) => roleOf(c) !== 'hidden');

  const title = titleColumn(cols);

  const rest = visible.filter((c) => c !== title);
  const subtitle = rest.find((c) => roleOf(c) === 'subtitle') || null;
  const badge = rest.find((c) => roleOf(c) === 'badge') || null;
  const pool = rest.filter((c) => c !== subtitle && c !== badge);

  // ── An empty fact YIELDS its slot; it never shrinks the card ──────────────
  //
  // The rule is substitution, not suppression, and the difference is the whole
  // correctness of it. `wanted` is exactly what the column ranking alone would
  // have drawn, so a card can only ever swap a blank fact for a populated one
  // further down the list — it can never end up saying LESS than it did.
  //
  // Both halves were measured, and getting only the first half right breaks
  // the second:
  //
  //   Office Jobs, a job with no client. Pool is Scheduled, Crew, Client,
  //   Property, Work list, Address. Ranking alone gives SCHEDULED · CREW ·
  //   "CLIENT —", and Property and Work list reach no phone card in the app.
  //   Substitution gives SCHEDULED · CREW · PROPERTY. Three facts either way.
  //
  //   Overview "Top jobs", a job nobody has worked. The pool is TWO columns,
  //   Sessions and Hours, and both are blank. There is nothing to substitute,
  //   so the dashes stay — and they should: OverviewInsight.test.js pins them
  //   as "a dash on the card too, never a zero it invented", and a card
  //   showing a title and nothing else cannot be told from one that failed to
  //   render.
  const perRow = row !== NO_ROW;
  const wanted = Math.min(pool.length, RECORD_FACT_LIMIT);
  let facts = pool.slice(0, wanted);
  if (perRow && wanted > 0) {
    const populated = pool.filter((c) => !isEmptyFact(cellValue(c, row, index)));
    // Populated first, blanks only to make up the number. A blank that has to
    // be shown is shown LAST, where it is read last, rather than in the middle
    // of the row it was ranked into.
    facts = populated.length >= wanted
      ? populated.slice(0, wanted)
      : [...populated, ...pool.filter((c) => !populated.includes(c))].slice(0, wanted);
  }

  return { title, subtitle, badge, facts };
}

/** Is a measured width too narrow for a table? */
export function isNarrowWidth(width) {
  return typeof width === 'number' && width > 0 && width < RECORD_CARD_MAX;
}

/** The fallback answer, from the viewport, for environments without ResizeObserver. */
export function viewportIsNarrow() {
  if (typeof window === 'undefined') return false;
  return isNarrowWidth(window.innerWidth);
}

/**
 * Watch an element's width and say whether it is card-narrow.
 *
 * Seeded from the viewport so the FIRST paint is already the right shape on a
 * phone — a ResizeObserver callback lands after paint, and a table flashing
 * into cards is the "browser stuffed into an app" feel the rebuild is removing.
 *
 * A measured width of 0 is ignored rather than treated as narrow. A list
 * inside a closed Sheet, a hidden tab panel or a not-yet-laid-out column
 * reports 0, and flipping every such list to cards on the strength of that
 * would make the shape depend on whether the user had opened the panel yet.
 */
export function useNarrowContainer(ref) {
  const [narrow, setNarrow] = useState(viewportIsNarrow);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const el = ref.current;
    const RO = window.ResizeObserver;

    if (el && typeof RO === 'function') {
      const measure = (width) => {
        if (typeof width === 'number' && width > 0) setNarrow(isNarrowWidth(width));
      };
      const ro = new RO((entries) => {
        for (const entry of entries || []) {
          const box = entry && entry.contentRect;
          measure(box && typeof box.width === 'number' ? box.width : el.clientWidth);
        }
      });
      ro.observe(el);
      measure(el.clientWidth);
      return () => ro.disconnect();
    }

    const onResize = () => setNarrow(viewportIsNarrow());
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [ref]);

  return narrow;
}

/**
 * The list. Same props DataTable has always taken, so the ~10 Office call
 * sites become responsive without being touched:
 *
 *   columns  [{ key, label, align, width, render(row, i), role }]
 *   rows     the records
 *   rowKey   string | (row, i) => key
 *   onRowClick, empty, dense, caption, className
 *
 * `role` is the only addition, and it is optional at every call site.
 */
export function RecordList({ columns = [], rows = [], rowKey, onRowClick, empty = 'Nothing to show', dense = false, caption, className = '', ...rest }) {
  const wrap = useRef(null);
  const narrow = useNarrowContainer(wrap);

  const keyOf = (row, i) => {
    if (typeof rowKey === 'function') return rowKey(row, i);
    if (typeof rowKey === 'string' && row && row[rowKey] !== undefined) return row[rowKey];
    if (row && row.id !== undefined) return row.id;
    return i;
  };
  const onKey = (row) => (e) => {
    if (!onRowClick) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row); }
  };

  // ── Narrow: one card per record ───────────────────────────────────────────
  // The whole card is the target when it is tappable, and it is a real
  // <button>, so Enter and Space come from the platform rather than from a
  // keydown handler that has to be remembered. Everything inside is a <span>:
  // a button's content model is phrasing content, and a <div> in there is the
  // kind of thing that renders fine until it does not.
  if (narrow) {
    return (
      <div
        ref={wrap}
        className={['fw-rl', className].filter(Boolean).join(' ')}
        data-layout="cards"
        {...rest}
      >
        {caption ? <span className="fw-sr-only">{caption}</span> : null}
        {rows.length === 0 ? (
          <p className="fw-rl__empty">{empty}</p>
        ) : rows.map((row, i) => {
          // Planned per ROW, not once for the list: which three facts are
          // worth drawing is a question about this record, and answering it
          // for the whole screen is what put "CLIENT \u2014" on a card while the
          // work list and the property never appeared at all.
          const plan = planCard(columns, row, i);
          const sub = plan.subtitle ? cellValue(plan.subtitle, row, i) : null;
          const body = (
            <>
              <span className="fw-rl__head">
                {plan.title ? <span className="fw-rl__title">{cellValue(plan.title, row, i)}</span> : null}
                {plan.badge ? <span className="fw-rl__badge">{cellValue(plan.badge, row, i)}</span> : null}
              </span>
              {/* An absent subtitle is an absent LINE, not a line holding a
                  dash. The address column reads through textOr, and a card
                  whose second line is an em dash tells a person the screen is
                  broken rather than that the job has no address on file. */}
              {isEmptyFact(sub) ? null : <span className="fw-rl__sub">{sub}</span>}
              {plan.facts.length ? (
                <span className="fw-rl__facts">
                  {plan.facts.map((col) => (
                    <span key={col.key} className="fw-rl__fact">
                      <span className="fw-rl__fact-label">{col.label}</span>
                      <span className={['fw-rl__fact-value', col.align === 'right' ? 'fw-num' : ''].filter(Boolean).join(' ')}>
                        {cellValue(col, row, i)}
                      </span>
                    </span>
                  ))}
                </span>
              ) : null}
            </>
          );
          return onRowClick ? (
            <button
              key={keyOf(row, i)}
              type="button"
              className="fw-rl__card fw-rl__card--tappable"
              onClick={() => onRowClick(row)}
            >
              {/* ── The card has to SAY it opens ───────────────────────────
                  Owner, 2026-09-18: "Don't see where admin can click on the
                  jobs."

                  MEASURED in a browser at 390x844 before this was added: the
                  card is a real <button> with `cursor: pointer` and a hover
                  background, and it contained ZERO svg elements. A cursor and
                  a hover are the two affordances a PHONE does not have, so a
                  tappable job card was pixel-identical to one that ignores
                  taps — the failure OfficePage's `recordById` comment names
                  from the other side, "a list that ignores clicks".

                  ── AND IT IS A SIBLING OF THE CONTENT, NOT PART OF THE HEAD ─
                  The first version put it inside `.fw-rl__head`, beside the
                  status pill. Re-measured at 390px: it took ~20px out of a row
                  that was already tight and the BADGE paid — "ACTIVE" went
                  from fitting to clipped. Clipping the status is the exact
                  complaint APP_REBUILD.md §3.3 exists to answer ("status is
                  never optional and never scrolled to"), so the fix for one
                  report had quietly reintroduced another.

                  Out here it is a column of its own, vertically centred, and
                  the headline row keeps every pixel it had.

                  aria-hidden because the <button> already announces itself; a
                  screen reader saying "chevron right" after the job title adds
                  nothing. */}
              <span className="fw-rl__card-main">{body}</span>
              <span className="fw-rl__go" aria-hidden="true"><Icon name="chevronRight" /></span>
            </button>
          ) : (
            <div key={keyOf(row, i)} className="fw-rl__card">{body}</div>
          );
        })}
      </div>
    );
  }

  // ── Wide: the table, unchanged ────────────────────────────────────────────
  // Byte-for-byte the markup DataTable shipped, including the .fw-table-wrap
  // `overflow-x: auto` box. That box is not the bug — a 9-column table in a
  // 900px pane still has to go somewhere — it was the bug only because it was
  // the ONLY answer, at every width.
  //
  // `data-scroll-x="ok"` is the opt-out scripts/ui-check.js requires of any
  // element that may scroll sideways, and this is the one place in the app
  // entitled to it. The reasoning is narrow and worth stating, because an
  // exemption is exactly how a check stops finding things:
  //
  //   this branch is UNREACHABLE below 768px of container width. On a phone
  //   RecordList renders `.fw-rl` cards and no `.fw-table-wrap` exists at all,
  //   so exempting this box cannot conceal a phone defect — the defect that
  //   started this rebuild (a 683px table in a 360px viewport) would now be
  //   cards, and the exemption would never be consulted.
  //
  // What it does cover is a genuinely wide table in a pane a person has
  // narrowed on a desktop — measured at 1440px with the job detail aside open,
  // where the Jobs table overflows its wrapper by 21px. Panning there is a
  // normal desktop affordance and is not what anybody complained about.
  //
  // If this is ever reached on a phone, the exemption is wrong, not the check.
  // Which cell reads as "this opens the record". A COLUMN rather than a new
  // trailing chevron cell, deliberately: this table already overflows its
  // wrapper by 36px at 1440px with the job detail aside open (measured
  // 2026-09-18 — the comment above says 21px and it has grown), and a column
  // that exists only to hold an affordance would push a real one further out
  // of reach. The cell costs no width at all.
  const opens = onRowClick ? titleColumn(columns) : null;

  return (
    <div
      ref={wrap}
      className={['fw-table-wrap', className].filter(Boolean).join(' ')}
      data-layout="table"
      data-scroll-x="ok"
      {...rest}
    >
      <table className={['fw-table', dense ? 'fw-table--dense' : '', onRowClick ? 'fw-table--tappable' : ''].filter(Boolean).join(' ')}>
        {caption ? <caption className="fw-sr-only">{caption}</caption> : null}
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={['fw-table__th', cellClass(col)].join(' ')}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className="fw-table__empty" colSpan={Math.max(1, columns.length)}>{empty}</td>
            </tr>
          ) : rows.map((row, i) => (
            <tr
              key={keyOf(row, i)}
              className="fw-table__row"
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={onRowClick ? onKey(row) : undefined}
              tabIndex={onRowClick ? 0 : undefined}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={['fw-table__td', cellClass(col),
                    onRowClick && col === opens ? 'fw-table__td--open' : ''].filter(Boolean).join(' ')}
                >
                  {cellValue(col, row, i)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default RecordList;
